import React, { useState, useRef, useEffect } from "react";
import {
    Modal,
    Button,
    Table,
    DatePicker,
    Space,
    message,
    Popconfirm,
    Form,
    Select,
    Row,
    Col,
    Dropdown,
    Menu,
    Card,
    Divider,
    Input,
    Spin,
    Typography,
    Skeleton,
} from "antd";
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    QuestionCircleOutlined,
    InfoCircleOutlined,
    UploadOutlined,
    FileTextOutlined,
    FileExcelOutlined,
    SyncOutlined,
    DownloadOutlined,
    SearchOutlined,
    WalletOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    CalculatorOutlined,
    HistoryOutlined,
} from "@ant-design/icons";
import { TrendingUp, TrendingDown, Info, PlayCircle, FileUp, FileDown, FileJson, FileSpreadsheet, FileText, Download, Share } from 'lucide-react';
import "./style.css";
import dayjs from "dayjs";
import TutorialModal from "./ModalTutorial";
import InstrucaoModal from "./ModalInstrucoes";
import { Method_POST_CONTEXTO, Method_GET_CONTEXTO_PDF } from "../../../../service/api";
import ImportarArquivo from "./ModalIncluirArquivo";
import { ModalLancamento } from "./ModalRecebimentosPagamentos";
import { ModalLancamentoEditar } from "./ModalLancamentoEditar";
import { ModalLancamentoEditarLote } from "./ModalEditarLancamentoLote";
import { useDraggableModal } from "../Balancete/utils/modalDraggable";

const { Option } = Select;

const StatCard = ({ title, value, icon, color, bgColor, loading }) => (
    <Card bordered={false} className="premium-card stat-card-entrance" style={{ height: '100%', padding: '12px' }}>
        <Skeleton loading={loading} active avatar paragraph={{ rows: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div className="stat-icon-wrapper" style={{ backgroundColor: bgColor, color: color }}>
                    {icon}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                        {title}
                    </div>
                    <div style={{
                        fontSize: 18,
                        fontWeight: 800,
                        color: value < 0 && title !== 'TOTAL DÉBITOS' ? '#e11d48' : '#0f172a',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>
                        {Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                    </div>
                </div>
            </div>
        </Skeleton>
    </Card>
);

export default function LancamentosBancarios({ visible, onClose, title, zIndex, onFocus }) {
    const draggableProps = useDraggableModal(zIndex, onFocus, {
        visible,
        modalKey: 'lancamentosBancarios',
        initialWidth: 1300,
        initialHeight: 'auto',
        overflow: 'hidden'
    });
    const [editingId, setEditingId] = useState(null);
    const [form] = Form.useForm();
    const [tutorialVisible, setTutorialVisible] = useState(false);
    const [instrucoesVisible, setInstrucoesVisible] = useState(false);
    const [contas, setContas] = useState([]);
    const [loadingContas, setLoadingContas] = useState(false);
    const [lancamentos, setLancamentos] = useState([]);
    const [saldo, setSaldo] = useState(null);
    const [loadingLancamentos, setLoadingLancamentos] = useState(false);
    const [loading, setLoading] = useState(false);
    const playerRef = useRef(null);
    const [incluirArquivoVisible, setIncluirArquivoVisible] = useState(false);
    const [tipoArquivo, setTipoArquivo] = useState(null);
    const [incluirLancamento, setIncluirLancamentos] = useState(false);
    const [tipoLancamento, setTipoLancamento] = useState(null);
    const [editarLancamento, setEditarLancamento] = useState(false);
    const [descricaoFiltro, setDescricaoFiltro] = useState("");
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [editarLoteVisible, setEditarLoteVisible] = useState(false);
    const [lancamentosSelecionados, setLancamentosSelecionados] = useState([]);
    const { RangePicker } = DatePicker;

    let clienteId = sessionStorage.getItem("COMPANY") ? JSON.parse(sessionStorage.getItem("COMPANY")).id : null

    // --- Carregar contas bancárias ---
    useEffect(() => {
        const fetchContas = async () => {
            try {
                setLoadingContas(true);
                const responseData = await Method_POST_CONTEXTO("/cliente/conta-bancaria/listar");
                const listaContas = Array.isArray(responseData) ? responseData : [];
                setContas([{ id: null, nome: "Todos" }, ...listaContas]);
            } catch (err) {
                console.error("Erro ao buscar contas bancárias:", err);
                message.error("Não foi possível carregar as contas bancárias");
                setContas([{ id: null, nome: "Todos" }]);
            } finally {
                setLoadingContas(false);
            }
        };
        // Só busca se a lista estiver vazia e estiver visível (primeira abertura)
        if (visible && contas.length === 0) fetchContas();
    }, [visible, contas.length]);

    // --- Buscar lançamentos ---
    const fetchLancamentos = async (params) => {
        const { dataInicio, dataTermino, contaBancaria } = params;

        if (!dayjs.isDayjs(dataInicio) || !dataInicio.isValid() ||
            !dayjs.isDayjs(dataTermino) || !dataTermino.isValid()) {
            console.warn("Período inválido. Abortando.");
            return { lancamentos: [], saldo: null };
        }

        setLoadingLancamentos(true);

        let todosLancamentos = [];
        let saldoFinal = null;

        try {
            // Normaliza para o começo do mês
            let currentMonth = dataInicio.startOf("month").clone();
            const endMonth = dataTermino.startOf("month").clone();

            while (currentMonth.isSame(endMonth) || currentMonth.isBefore(endMonth)) {

                const payload = {
                    dataInicio: currentMonth.startOf("month").toISOString(),
                    dataTermino: currentMonth.endOf("month").toISOString(),
                    ...(contaBancaria
                        ? { lancamento: { contaBancaria: { id: contaBancaria } } }
                        : {}),
                };

                const response = await Method_POST_CONTEXTO(
                    "/contabil/lancamento/pesquisar",
                    payload
                );

                // Junta lançamentos
                if (Array.isArray(response?.lancamentos)) {
                    todosLancamentos.push(...response.lancamentos);
                }

                // Saldo do último mês
                if (currentMonth.isSame(endMonth, "month")) {
                    saldoFinal = response?.saldo ?? null;
                }

                currentMonth = currentMonth.add(1, "month");
            }

            setLancamentos(todosLancamentos || []);
            setSaldo(saldoFinal || null);

        } catch (err) {
            console.error("Erro ao buscar lançamentos:", err);
            message.error("Erro ao carregar lançamentos do período.");
            return { lancamentos: [], saldo: null };
        } finally {
            setLoadingLancamentos(false);
        }
    };


    // --- Funções de Lançamento ---
    const handleSalvar = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                ...values,
                id: editingId || Date.now(),
                dataReferencia: values.dataReferencia.toISOString(),
                valor: Number(values.valor),
            };
            if (editingId) {
                setLancamentos((prev) =>
                    prev.map((item) => (item.id === editingId ? { ...item, ...payload } : item))
                );
                message.success("Lançamento atualizado!");
            } else {
                setLancamentos((prev) => [...prev, payload]);
                message.success("Lançamento criado!");
            }
            form.resetFields();
            setEditingId(null);
        } catch {
            message.error("Preencha todos os campos obrigatórios");
        }
    };

    const handleDeletar = async (record) => {
        try {
            setLancamentos((prev) => prev.filter((item) => item.id !== record.id));
            // dataReferencia vai junto pra o backend decidir se remove no Pantheon (>=2026).
            const payload = { id: record.id, dataReferencia: record.dataReferencia };
            await Method_POST_CONTEXTO("/contabil/lancamento/remover", payload);
            message.success("Lançamento removido!");
        } catch (err) {
            console.error("Erro ao remover lançamento:", err);
            message.error("Não foi possível remover o lançamento");
        }
    };

    const handleTutorialClose = () => {
        if (playerRef.current) playerRef.current.stopVideo();
        setTutorialVisible(false);
    };

    // --- Colunas da tabela ---
    const columns = [
        { 
            title: "Data", 
            dataIndex: "dataReferencia", 
            key: "data", 
            width: 110,
            render: (v) => dayjs(v).format("DD/MM/YYYY") 
        },
        { 
            title: "Categoria", 
            dataIndex: "categoria", 
            key: "categoria", 
            width: 180,
            render: (c) => c?.nome || "-" 
        },
        { 
            title: "Descrição", 
            dataIndex: "descricao", 
            key: "descricao",
            ellipsis: true,
        },
        {
            title: "Crédito",
            dataIndex: "valor",
            key: "credito",
            width: 120,
            align: 'right',
            render: (v, r) => r.tipo?.id === "C" ? <span style={{ color: "green", fontWeight: 600 }}>{Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</span> : null,
        },
        {
            title: "Débito",
            dataIndex: "valor",
            key: "debito",
            width: 120,
            align: 'right',
            render: (v, r) => r.tipo?.id === "D" ? <span style={{ color: "red", fontWeight: 600 }}>{Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</span> : null,
        },
        {
            title: "Ações",
            key: "acoes",
            width: 100,
            align: 'center',
            render: (_, rec) => (
                <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => { setEditingId(rec.id); setEditarLancamento(true); }} />
                    <Popconfirm title="Deseja realmente excluir?" onConfirm={() => handleDeletar(rec)}>
                        <Button size="small" icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // --- Menus ---
    const importMenu = (
        <Menu className="premium-dropdown-menu">
            <Menu.Item
                key="ofx"
                icon={<FileJson size={16} color="#2563eb" />}
                onClick={() => { setTipoArquivo("ofx"); setIncluirArquivoVisible(true); }}
            >
                Conciliar OFX
            </Menu.Item>
            <Menu.Item
                key="excel"
                icon={<FileSpreadsheet size={16} color="#16a34a" />}
                onClick={() => { setTipoArquivo("excel"); setIncluirArquivoVisible(true); }}
            >
                Importar Excel
            </Menu.Item>
            <Menu.Item
                key="csv"
                icon={<FileText size={16} color="#64748b" />}
                disabled
            >
                Importar CSV
            </Menu.Item>
        </Menu>
    );

    const exportMenu = (
        <Menu onClick={({ key }) => handleExport(key)} className="premium-dropdown-menu">
            <Menu.Item key="excel" icon={<FileSpreadsheet size={16} color="#16a34a" />}>
                Exportar Excel
            </Menu.Item>
            <Menu.Item key="csv" icon={<FileText size={16} color="#64748b" />}>
                Exportar CSV
            </Menu.Item>
            <Menu.Item key="ofx" icon={<FileJson size={16} color="#2563eb" />}>
                Exportar OFX
            </Menu.Item>
        </Menu>
    );

    // --- Descrições únicas ---
    const descricoes = [...new Set(lancamentos.map((l) => l.descricao).filter(Boolean))];

    // --- Filtragem ---
    const lancamentosFiltrados = descricaoFiltro
        ? lancamentos.filter((l) => l.descricao?.toLowerCase().includes(descricaoFiltro.toLowerCase()))
        : lancamentos;

    // --- Totais ---
    const creditos = lancamentosFiltrados.filter(
        (l) => l.tipo?.id === "C" || l.categoria?.tipo?.id === "C"
    );
    const debitos = lancamentosFiltrados.filter(
        (l) => l.tipo?.id === "D" || l.categoria?.tipo?.id === "D"
    );
    const totalCreditos = creditos.reduce((acc, l) => acc + Number(l.valor), 0);
    const totalDebitos = debitos.reduce((acc, l) => acc + Number(l.valor), 0);
    const saldoAnterior = saldo ? saldo.creditos + saldo.debitos : 0;
    const saldoFinal = totalCreditos - totalDebitos + saldoAnterior;

    // --- Row Selection para edição em lote ---
    const rowSelection = {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
    };

    const handleEditarLote = () => {
        if (selectedRowKeys.length === 0) return message.warning("Selecione ao menos um lançamento");

        const selecionados = lancamentos.filter((l) => selectedRowKeys.includes(l.id));
        setLancamentosSelecionados(selecionados);
        setEditarLoteVisible(true);
    };

    const gerarLancamento = async (record) => {
        setLoading(true);
        try {
            if (record.dataReferencia && !record.dataInicio) {
                setLancamentos((prev) =>
                    prev.filter((item) => item.id !== record.id)
                );

                const payload = {
                    dataReferencia: record.dataReferencia,
                    empresa: { id: clienteId },
                    ...(record.contaBancaria ? { contaBancaria: { id: record.contaBancaria } } : {})
                };

                await Method_POST_CONTEXTO(
                    "empresa/contabilidade/lancamento/cadastrar/banco",
                    payload
                );

                message.success("Lançamento gerado com sucesso!");
                return true;
            }

            if (record.dataInicio && record.dataFim) {
                let dataAtual = dayjs(record.dataInicio).startOf("month");
                const dataFinal = dayjs(record.dataFim).startOf("month");

                while (
                    dataAtual.isBefore(dataFinal) ||
                    dataAtual.isSame(dataFinal)
                ) {

                    const payload = {
                        dataReferencia: dataAtual.toISOString(),
                        empresa: { id: clienteId },
                        ...(record.contaBancaria ? { contaBancaria: { id: record.contaBancaria } } : {})
                    };

                    await Method_POST_CONTEXTO(
                        "empresa/contabilidade/lancamento/cadastrar/banco",
                        payload
                    );

                    dataAtual = dataAtual.add(1, "month");
                }

                message.success("Lançamentos gerados para todos os meses!");
                return true;
            }

        } catch (err) {
            console.error("Erro ao gerar lançamento:", err);
            message.error("Não foi possível gerar o lançamento");
            return false;
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async (type) => {
        try {
            const periodo = form.getFieldValue("periodo");
            if (!periodo?.length) return message.warning("Selecione um período para exportar");

            message.loading({ content: `Gerando arquivo ${type.toUpperCase()}...`, key: 'exporting' });

            const start = periodo[0].startOf("month").format('YYYY-MM-DD');
            const end = periodo[1].endOf("month").format('YYYY-MM-DD');

            const url = `/contabil/lancamento/export/${type}?dataInicio=${start}&dataTermino=${end}`;

            const response = await Method_GET_CONTEXTO_PDF(url);

            if (response.data) {
                const blob = new Blob([response.data], {
                    type: response.headers['content-type'] || 'application/octet-stream'
                });
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;

                const extension = type === 'excel' ? 'xlsx' : type;
                link.setAttribute('download', `lancamentos_${start}_a_${end}.${extension}`);

                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(downloadUrl);

                message.success({ content: `Arquivo ${type.toUpperCase()} gerado com sucesso!`, key: 'exporting' });
            }
        } catch (error) {
            console.error(`Erro ao exportar ${type}:`, error);
            message.error({ content: `Erro ao gerar arquivo ${type.toUpperCase()}.`, key: 'exporting' });
        }
    };


    const { Title, Text } = Typography;

    return (
        <Spin spinning={loading} tip="Gerando lançamentos...">
            <>

                <Modal
                    title={title || "Lançamentos Bancários"}
                    open={visible}
                    onCancel={onClose}
                    footer={null}
                    width={1300}
                    className="premium-modal-v2"
                    {...draggableProps}
                    styles={{
                        body: {
                            maxHeight: "80vh",
                            padding: '24px',
                            overflowY: "auto"
                        }
                    }}
                >
                    <div style={{ marginBottom: 24 }}>
                        <Row gutter={[16, 16]} align="middle" justify="space-between">
                            <Col xs={24} md={12}>
                                <Space direction="vertical" size={0}>
                                    <Title level={3} style={{ margin: 0, color: '#0f172a', fontWeight: 800 }}>
                                        Lançamentos Bancários
                                    </Title>
                                    <Text type="secondary" style={{ fontSize: '14px' }}>
                                        Controle e conciliação de extratos bancários.
                                    </Text>
                                </Space>
                            </Col>
                        </Row>
                    </div>

                    <Space direction="vertical" size={24} style={{ width: "100%" }}>
                        <Card className="premium-card">
                            <Form
                                form={form}
                                layout="vertical"
                                initialValues={{ dataReferencia: dayjs(), contaBancaria: null, descricao: "" }}
                                onValuesChange={(_, allValues) => {
                                    const periodo = allValues.periodo;
                                    if (periodo?.length === 2) {
                                        fetchLancamentos({
                                            dataInicio: periodo[0],
                                            dataTermino: periodo[1],
                                            contaBancaria: allValues.contaBancaria
                                        });
                                    }
                                }}
                            >
                                <Row gutter={[16, 16]} align="bottom">
                                    <Col xs={24} sm={12} md={4}>
                                        <Form.Item label="Período" name="periodo" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                                            <RangePicker picker="month" format="MM/YYYY" style={{ width: "100%", borderRadius: 8 }} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={4}>
                                        <Form.Item label="Conta Bancária" name="contaBancaria" rules={[{ required: true, message: "Selecione a conta" }]} style={{ marginBottom: 0 }}>
                                            <Select loading={loadingContas} style={{ width: '100%' }}>
                                                {contas.map((c) => (
                                                    <Option key={c.id} value={c.id}>
                                                        {c.id === null ? "Todos" : `${c.banco?.nome || "Banco"} - CC ${c.conta}`}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={24} md={4}>
                                        <Form.Item label="Descrição" name="descricao" style={{ marginBottom: 0 }}>
                                            <Select
                                                showSearch
                                                allowClear
                                                placeholder="Buscar por descrição"
                                                value={descricaoFiltro}
                                                onSearch={(val) => setDescricaoFiltro(val)}
                                                onChange={(val) => setDescricaoFiltro(val)}
                                                filterOption={false}
                                            >
                                                {descricoes.map((desc, i) => (
                                                    <Option key={i} value={desc}>{desc}</Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={4}>
                                        <Button
                                            type="primary"
                                            icon={<TrendingUp size={18} />}
                                            className="btn-receita-gradient"
                                            block
                                            onClick={(e) => { e.stopPropagation(); setTipoLancamento("C"); setIncluirLancamentos(true); }}
                                        >
                                            Receita
                                        </Button>
                                    </Col>
                                    <Col xs={24} sm={12} md={4}>
                                        <Button
                                            type="primary"
                                            icon={<TrendingDown size={18} />}
                                            className="btn-despesa-gradient"
                                            block
                                            onClick={() => { setTipoLancamento("D"); setIncluirLancamentos(true); }}
                                        >
                                            Despesa
                                        </Button>
                                    </Col>
                                </Row>
                                <Row gutter={[24, 24]} align="bottom" style={{ marginTop: 24 }}>
                                    <Col xs={24} md={24}>
                                        <Space size={8} wrap style={{ width: '100%', justifyContent: 'flex-start' }}>
                                            <Button
                                                type="link"
                                                icon={<EditOutlined />}
                                                onClick={handleEditarLote}
                                                style={{ color: '#6366f1', fontWeight: 600, padding: '4px 8px' }}
                                            >
                                                Editar em Lote
                                            </Button>
                                            <Button
                                                loading={loading}
                                                icon={<SyncOutlined />}
                                                onClick={async () => {
                                                    const periodo = form.getFieldValue("periodo");
                                                    const contaBancaria = form.getFieldValue("contaBancaria");
                                                    if (!periodo?.length) return message.warning("Selecione um período");
                                                    
                                                    const success = await gerarLancamento({
                                                        dataInicio: periodo[0].toISOString(),
                                                        dataFim: periodo[1].toISOString(),
                                                        empresa: { id: clienteId },
                                                        contaBancaria: contaBancaria
                                                    });

                                                    if (success) {
                                                        fetchLancamentos({
                                                            dataInicio: periodo[0],
                                                            dataTermino: periodo[1],
                                                            contaBancaria: contaBancaria
                                                        });
                                                    }
                                                }}
                                                className="premium-btn-action"
                                                style={{ height: '38px' }}
                                            >
                                                Gerar
                                            </Button>
                                            
                                            <Button
                                                loading={loading}
                                                icon={<HistoryOutlined />}
                                                onClick={async () => {
                                                    const periodo = form.getFieldValue("periodo");
                                                    const contaBancaria = form.getFieldValue("contaBancaria");
                                                    if (!periodo?.length) return message.warning("Selecione um período");

                                                    setLoading(true);
                                                    try {
                                                        const start = periodo[0].startOf("month").format('YYYY-MM-DD');
                                                        const end = periodo[1].endOf("month").format('YYYY-MM-DD');
                                                        const url = `/empresa/contabilidade/regerar/periodo?dataInicio=${start}&dataTermino=${end}&types=LANCAMENTO_BANCARIO`;
                                                        
                                                        await Method_POST_CONTEXTO(url, {});
                                                        message.success("Regeração de lançamentos iniciada!");
                                                        
                                                        fetchLancamentos({
                                                            dataInicio: periodo[0],
                                                            dataTermino: periodo[1],
                                                            contaBancaria: contaBancaria
                                                        });
                                                    } catch (err) {
                                                        console.error(err);
                                                        message.error("Erro ao solicitar regeração");
                                                    } finally {
                                                        setLoading(false);
                                                    }
                                                }}
                                                className="premium-btn-action"
                                                style={{ height: '38px', color: '#f59e0b' }}
                                            >
                                                Regerar
                                            </Button>

                                            <Dropdown overlay={importMenu} trigger={['click']} placement="bottomRight">
                                                <Button icon={<FileUp size={18} />} className="btn-import-premium">
                                                    Importar
                                                </Button>
                                            </Dropdown>
                                            <Dropdown overlay={exportMenu} trigger={['click']} placement="bottomRight">
                                                <Button icon={<FileDown size={18} />} className="btn-export-premium">
                                                    Exportar
                                                </Button>
                                            </Dropdown>
                                        </Space>
                                    </Col>
                                </Row>

                            </Form>
                        </Card>

                        <div style={{ marginTop: 8 }}>
                            <Row gutter={[16, 16]}>
                                <Col xs={24} sm={12} lg={6}>
                                    <StatCard
                                        title="Saldo Anterior"
                                        value={saldoAnterior}
                                        icon={<WalletOutlined />}
                                        color="#4f46e5"
                                        bgColor="#eef2ff"
                                        loading={loadingLancamentos}
                                    />
                                </Col>
                                <Col xs={24} sm={12} lg={6}>
                                    <StatCard
                                        title="Total Créditos"
                                        value={totalCreditos}
                                        icon={<ArrowUpOutlined />}
                                        color="#059669"
                                        bgColor="#ecfdf5"
                                        loading={loadingLancamentos}
                                    />
                                </Col>
                                <Col xs={24} sm={12} lg={6}>
                                    <StatCard
                                        title="Total Débitos"
                                        value={totalDebitos}
                                        icon={<ArrowDownOutlined />}
                                        color="#e11d48"
                                        bgColor="#fff1f2"
                                        loading={loadingLancamentos}
                                    />
                                </Col>
                                <Col xs={24} sm={12} lg={6}>
                                    <StatCard
                                        title="Saldo Final"
                                        value={saldoFinal}
                                        icon={<CalculatorOutlined />}
                                        color="#d97706"
                                        bgColor="#fffbeb"
                                        loading={loadingLancamentos}
                                    />
                                </Col>
                            </Row>
                        </div>

                        <Card className="premium-card" bodyStyle={{ padding: 0 }}>
                            <Table
                                dataSource={lancamentosFiltrados}
                                loading={loadingLancamentos}
                                rowKey="id"
                                columns={columns}
                                rowSelection={rowSelection}
                                pagination={{ pageSize: 15 }}
                                size="middle"
                                className="premium-table"
                                scroll={{ x: 1200 }}
                                sticky
                            />
                        </Card>
                    </Space>
                </Modal>

                {tutorialVisible && <TutorialModal open={tutorialVisible} onClose={handleTutorialClose} />}
                {instrucoesVisible && <InstrucaoModal open={instrucoesVisible} onClose={() => setInstrucoesVisible(false)} />}
                {incluirArquivoVisible && <ImportarArquivo open={incluirArquivoVisible} onClose={() => setIncluirArquivoVisible(false)} tipoArquivo={tipoArquivo} />}
                {incluirLancamento && <ModalLancamento open={incluirLancamento} tipo={tipoLancamento} onClose={() => setIncluirLancamentos(false)} onSave={(values) => setIncluirLancamentos(false)} />}
                {editarLancamento && <ModalLancamentoEditar open={editarLancamento} zIndex={zIndex ? zIndex + 2 : 1100} onFocus={onFocus} onClose={() => setEditarLancamento(false)} lancamento={lancamentos.find((l) => l.id === editingId)} />}
                {editarLoteVisible && (
                    <ModalLancamentoEditarLote
                        open={editarLoteVisible}
                        zIndex={zIndex ? zIndex + 2 : 1100}
                        onFocus={onFocus}
                        onClose={async () => { setEditarLoteVisible(false); setSelectedRowKeys([]); const filtrosAtuais = form.getFieldsValue(); await fetchLancamentos(filtrosAtuais); }}
                        lancamentos={lancamentosSelecionados}
                        onSave={async (atualizados) => { setLancamentos((prev) => prev.map((l) => atualizados.find((u) => u.id === l.id) || l)); setSelectedRowKeys([]); setEditarLoteVisible(false); const filtrosAtuais = form.getFieldsValue(); await fetchLancamentos(filtrosAtuais); }}
                    />
                )}

            </>
        </Spin >
    );
}
